/**
 * PageScopeTabs
 *
 * Page-level scope/lifecycle navigation. Used to switch between
 * mutually-exclusive datasets (Active / Archived / Templates / etc.)
 * that fundamentally change what a page shows: different stats,
 * different actions, different empty-state copy.
 *
 * NOT a filter. Use a filter drawer/control for composable narrowing
 * (cohort, program, status, search, …) WITHIN the chosen scope.
 *
 * Visual: underline tabs (Lever / Greenhouse / GitHub / Linear style).
 * Reads unambiguously as page navigation, not a toggle/filter.
 *
 * Accessibility:
 *   - `role="tablist"` landmark with `aria-label` for screen readers
 *   - Keyboard: ←/→ to move focus between tabs, Home/End to jump,
 *     Enter/Space to activate (we use automatic activation — focus
 *     IS selection, matching Radix's default tab behavior)
 *   - Each tab: `role="tab"`, `aria-selected`, `tabIndex` managed so
 *     only the selected tab is in the natural tab order
 *
 * Generic over the scope value type so each page can declare its own
 * typed enum (`PipelineScope`, `CohortsScope`, `VettingScope`, …)
 * without losing exhaustiveness checks.
 *
 * Counts on each tab are optional — pass them when you have them so
 * the inactive tab still shows its size (admins want "Archived (47)"
 * to know there's something there before clicking).
 */

import { useCallback, useId, useMemo, useRef } from 'react';
import { cn } from '../lib/utils';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

export interface PageScopeTab<T extends string> {
  /** Discriminator used in `value` / `onChange`. */
  value: T;
  /** Visible label — i18n'd by the caller. */
  label: string;
  /**
   * Count badge appended as `(N)`. Omit to render the label alone
   * (useful when the count is genuinely unknown or not yet loaded).
   */
  count?: number;
  /** Disable activation. Tab is still focusable for screen readers. */
  disabled?: boolean;
}

export interface PageScopeTabsProps<T extends string> {
  /** The available scopes for this page. Order = visual order. */
  tabs: ReadonlyArray<PageScopeTab<T>>;
  /** The currently active scope. Must match one of `tabs[].value`. */
  value: T;
  /** Fires when the user picks a different scope. */
  onChange: (next: T) => void;
  /**
   * Required for screen readers. Identifies the tablist's purpose
   * (typically the page name — "Pipeline scope", "Cohorts scope").
   */
  ariaLabel: string;
  /** Optional className for the outer container. */
  className?: string;
}

// ═══════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════

export function PageScopeTabs<T extends string>({
  tabs,
  value,
  onChange,
  ariaLabel,
  className,
}: PageScopeTabsProps<T>) {
  const tabRefs = useRef<Map<T, HTMLButtonElement>>(new Map());
  const tablistId = useId();

  // Roving-focus + arrow-key navigation.
  // Keyboard contract (matches WAI-ARIA tablist authoring practices):
  //   ←/Home  → move focus to previous / first non-disabled tab
  //   →/End   → move focus to next / last non-disabled tab
  // Activation is automatic — focusing a tab activates it. This is
  // the friendlier behavior for a small static set (≤5 tabs), per
  // WAI-ARIA: "if the tab content is loaded quickly, automatic
  // activation is preferred."
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      const enabled = tabs.filter((t) => !t.disabled);
      if (enabled.length === 0) return;

      const currentIdx = enabled.findIndex((t) => t.value === value);
      let nextIdx: number;
      switch (e.key) {
        case 'ArrowLeft':
          nextIdx = (currentIdx - 1 + enabled.length) % enabled.length;
          break;
        case 'ArrowRight':
          nextIdx = (currentIdx + 1) % enabled.length;
          break;
        case 'Home':
          nextIdx = 0;
          break;
        case 'End':
          nextIdx = enabled.length - 1;
          break;
        default:
          return;
      }
      if (nextIdx === currentIdx) return;
      e.preventDefault();
      const target = enabled[nextIdx];
      onChange(target.value);
      // Move focus to the newly-selected tab so the visual focus ring
      // follows the selection — matches user expectation for arrow nav.
      requestAnimationFrame(() => {
        tabRefs.current.get(target.value)?.focus();
      });
    },
    [tabs, value, onChange]
  );

  const tabsWithIds = useMemo(
    () =>
      tabs.map((tab) => ({
        ...tab,
        tabId: `${tablistId}-tab-${tab.value}`,
      })),
    [tabs, tablistId]
  );

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      // Underline tab bar — full-width baseline, individual tabs
      // sit on top with their own bottom-border that "wins" when
      // active. Reads as page navigation, not a toggle.
      // No margin of its own: each tab's `-mb-px` meets the baseline, and a
      // bar margin here overrode the page's `space-y-*` gap (Tailwind v4
      // spaces by the child's bottom margin), sitting the bar flush on the
      // KPI row below it (BW-02/45).
      className={cn('flex items-center gap-1 border-b border-border', className)}
    >
      {tabsWithIds.map((tab) => {
        const isActive = tab.value === value;
        return (
          <button
            key={tab.value}
            ref={(el) => {
              if (el) tabRefs.current.set(tab.value, el);
              else tabRefs.current.delete(tab.value);
            }}
            type="button"
            role="tab"
            id={tab.tabId}
            data-testid={`page-scope-tab-${tab.value}`}
            /**
             * The count as DATA, not as text. (W7.1 · block 39)
             *
             * The badge is `aria-hidden` and the accessible count lives in an
             * `sr-only` span, so a test reading either is reading presentation.
             * Worse, both are formatted, so a locale that groups thousands
             * would break an assertion that has nothing to do with locale.
             *
             * This is also the lesson from the Tailwind variant-class false
             * positive: never assert state from a rendered class or a rendered
             * string when the component can simply state the fact.
             */
            data-count={tab.count}
            aria-selected={isActive}
            aria-disabled={tab.disabled || undefined}
            // Roving tabindex — only the selected tab is in the
            // natural tab order. The others receive focus via arrow
            // keys (handled by onKeyDown above). This is the correct
            // tablist pattern; without it screen-reader / keyboard
            // users would tab through every tab individually,
            // which is noisy.
            tabIndex={isActive ? 0 : -1}
            disabled={tab.disabled}
            onClick={() => {
              if (tab.disabled || isActive) return;
              onChange(tab.value);
            }}
            onKeyDown={onKeyDown}
            className={cn(
              'relative inline-flex items-center gap-1.5 px-4 py-2.5',
              'text-sm font-medium transition-colors',
              'border-b-2 -mb-px', // own bottom border that overlaps the tablist baseline
              'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-t-md',
              isActive
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border',
              'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-muted-foreground'
            )}
          >
            <span>{tab.label}</span>
            {typeof tab.count === 'number' && (
              <span
                className={cn(
                  'inline-flex items-center justify-center rounded-full px-1.5 min-w-[1.25rem] h-5',
                  'text-xs font-medium tabular-nums',
                  // `text-primary` on a 10%-primary wash measures 3.5:1 against
                  // the dark surface — under the 4.5:1 WCAG AA minimum for text
                  // this size. The badge is `aria-hidden` (the count is
                  // re-announced in the accessible label below), but it is still
                  // READ by sighted users, so contrast is a real requirement,
                  // not a technicality. `text-foreground` on the same wash keeps
                  // the tinted look and clears the bar.
                  isActive ? 'bg-primary/10 text-foreground' : 'bg-muted text-muted-foreground'
                )}
                aria-hidden="true"
              >
                {tab.count}
              </span>
            )}
            {/*
              SR-friendly count — the visible badge above is decorative
              (aria-hidden), and we re-announce the count inside the
              accessible label so screen readers say
              "Active, 12 items, tab" instead of just "Active, tab".
            */}
            {typeof tab.count === 'number' && <span className="sr-only">{`(${tab.count})`}</span>}
          </button>
        );
      })}
    </div>
  );
}
